import uuidv4 from "uuid/v4";
import jwt_decode from "jwt-decode";
import * as types from "../mutation-types";
import { BifrostBackendNestApi, BifrostBackendApi } from "../../gateways/BifrostBackendApi";
import OrdersProvider from "../../providers/OrdersProvider";
import OrderCorrespondenceProvider from "../../providers/OrderCorrespondenceProvider";
import check from "../../domains2/libraries/check-types-ext";
import { copyObject } from "../../libraries/utils";
import OrderGenerateProvider from "../../providers/OrderGenerateProvider";
import AuthenticationProvider from "../../providers/AuthenticationProvider";
import ProductCatalogProvider from "../../providers/ProductCatalogProvider";
import { store } from "../index";
import AdminHomePageLinksProvider from "../../providers/AdminHomePageLinksProvider";

const getApi = () => {
	const useNestOrderPaymentFlow =
		store.getters.featureToggles &&
		store.getters.featureToggles.useNestOrderPaymentFlow;
	return useNestOrderPaymentFlow ? BifrostBackendNestApi : BifrostBackendApi;
};

const initialOrderForm = Object.freeze({
	productSelections: [],
	shopData: {},
	pickupData: {
		pickupDate: null,
		pickupTime: null
	},
	customerDetails: {
		name: "",
		email: "",
		telephone: ""
	},
	mustBeHeated: false,
	specialNotes: "",
	userConsent: false,
	vouchersRedeemed: [],
	tenantSpecificData: {}
});

const initialBulkOrder = [];

const selectEditingBulkOrder = (state) => {
	return state.persistedData.orderFormBulk[state.indexOfEditingOrderFromBulk] ; };

// initial state
const state = {
	persistedData: {
		jwtToken: null,
		dateFilter: "week",
		orderForm: copyObject(initialOrderForm),
		orderFormBulk: initialBulkOrder,
		products: {
			selectedProductId: null
		},
		catalogs: {
			selectedCatalogId: null,
			tempCatalog: null,
			isCatalogPagesMainLoading: false
		},
		azureADLoginState: "",
		googleAuthState: {
			externalGoogleEmail: "",
			externalGoogleId: ""
		},
		adAuthState: {
			externalAzureId: "",
			userData: {},
			roles: []
		},
		jwtToken2: null
	},
	selectedCatalog: null,
	redirectUrl: null,
	adminDetails: null,
	loadedOrders: [],
	pendingOrders: [],
	selectDepartmentId: null,
	catalogEditingState: false,
	productEditingState: false,
	indexOfEditingOrderFromBulk: 0,
	isNavBarOpen: false,
	navMenuItems: [],
	productMassEditingState: false,
	productCatalogByVersions: {},
	selectedVersion: 0,
	isAdminTopMenuShown: true,
	adminHomePageLinks: [],
	productSnackBar: {
		message: "",
		isShown: false
	},
	isInitGboApp: true
};

// getters
const getters = {
	jwtToken: state => state.persistedData.jwtToken,
	jwtTokenDecoded: state => {
		if (typeof state.persistedData.jwtToken === "string" && state.persistedData.jwtToken.length > 0) {
			try {
				return jwt_decode(state.persistedData.jwtToken);
			} catch (e) {
				return undefined;
			}
		}

		return undefined;
	},
	dateFilter: state => state.persistedData.dateFilter,
	loadedOrders: state => state.loadedOrders,
	pendingOrders: state => state.pendingOrders,
	adminDetails: state => state.adminDetails,
	shopDetails: state => state.adminDetails,
	redirectUrl: state => state.redirectUrl,
	selectDepartmentId: state => state.selectDepartmentId,
	orderForm: state => state.persistedData.orderForm,
	orderFormBulk: state => state.persistedData.orderFormBulk,
	editingOrderFormBulk: selectEditingBulkOrder,
	selectedProductId: state => state.persistedData.products.selectedProductId,
	catalogEditingState: state => state.catalogEditingState,
	tempCatalog: state => state.persistedData.catalogs.tempCatalog,
	selectedCatalog: state => state.selectedCatalog,
	selectedCatalogId: state => state.persistedData.catalogs.selectedCatalogId,
	productEditingState: state => state.productEditingState,
	azureADLoginState: state => state.persistedData.azureADLoginState,
	isCatalogPagesMainLoading: state => state.persistedData.catalogs.isCatalogPagesMainLoading,
	isNavBarOpen: state => state.isNavBarOpen,
	productMassEditingState: state => state.productMassEditingState,
	productCatalogByVersions: state => state.productCatalogByVersions,
	selectedVersion: state => !state.selectedVersion ? store.getters.activeVersion: state.selectedVersion,
	topAdminMenuShown: state => state.isAdminTopMenuShown,
	productSnackBarMessage: state => state.productSnackBar.message,
	productSnackBarIsShown: state => state.productSnackBar.isShown,
	isInitGboApp: state => state.isInitGboApp,
	googleAuthState: state => state.googleAuthState,
	adAuthState: state => state.adAuthState,
	adminHomePageLinks: state => state.adminHomePageLinks,
	navMenuItems: state => state.navMenuItems,
	jwtToken2: state => state.persistedData.jwtToken2
};

// actions
const actions = {
	async adminLogin({ commit, dispatch }, { shopId, tenantId, password }) {
		const response = await BifrostBackendNestApi.post(`/authentication/local`, {
			shopNumber: `${tenantId}_${shopId}`,
			password: password,
			strategy: "local"
		});

		commit(types.ADMIN_SET_JWT_TOKEN, response.data.accessToken);
		await dispatch("jwt1ToJwt2", {accessToken: response.data.accessToken});
		return dispatch("cacheAdminDetails");
	},

	async adminADLogin({ dispatch }, { tenantId, shopNumber, externalAzureId, roles, userData }) {
		const response = await AuthenticationProvider.authenticateAzureAdSelectedShop({
			tenantId,
			shopNumber,
			externalAzureId,
			roles,
			userData
		});
		await dispatch("jwt1ToJwt2", {accessToken: response.data.accessToken});
		return dispatch("cacheJwtToken", {accessToken: response.data.accessToken});
	},

	async adminGoogleLogin({ dispatch }, { tenantId, shopNumber, externalGoogleId, externalGoogleEmail }) {
		const response = await AuthenticationProvider.authenticateGoogleSelectedShop({
			tenantId: tenantId,
			shopNumber: shopNumber,
			externalGoogleId: externalGoogleId,
			externalGoogleEmail: externalGoogleEmail
		});

		await dispatch("jwt1ToJwt2", {accessToken: response.data.accessToken});
		return dispatch("cacheJwtToken", {accessToken: response.data.accessToken});
	},

	cacheJwtToken({commit, dispatch}, {accessToken}) {
		commit(types.ADMIN_SET_JWT_TOKEN, accessToken);
		return dispatch("cacheAdminDetails");
	},

	cacheAdminDetails({ commit, getters }, options = {}) {
		const {force = false} = options;
		if (getters.adminDetails && force !== true) {
			return Promise.resolve();
		} else {
			return getApi().get("/shop-admin/logged", {
				headers: { Authorization: getters.jwtToken }
			}).then(response => {
				commit(types.ADMIN_SET_ADMIN_DETAILS, response.data);
			}).catch(() => {
				commit(types.ADMIN_SET_JWT_TOKEN, null);
			});
		}
	},

	async confirmOrder({ commit, getters }, { id, changeStatusToken }) {
		const useNestOrderPaymentFlow = store.getters.featureToggles.useNestOrderPaymentFlow;
		const headers = useNestOrderPaymentFlow ? { Authorization: getters.jwtToken } : {};

		const response = await getApi().put(`/change-order-status/${id}`, {
			token: changeStatusToken,
			status: "confirmed"
		}, { headers });
		commit(types.ADMIN_ORDER_CONFIRM, id);
		return response;
	},

	async rejectOrder({ commit, getters }, { id, changeStatusToken, rejectData }) {
		const useNestOrderPaymentFlow = store.getters.featureToggles.useNestOrderPaymentFlow;
		const headers = useNestOrderPaymentFlow ? { Authorization: getters.jwtToken } : {};

		const response = await getApi().put(`/change-order-status/${id}`, {
			token: changeStatusToken,
			status: "rejected",
			rejectData: rejectData
		}, { headers });

		commit(types.ADMIN_ORDER_REJECT, id);
		return response;
	},

	upsertOrder({ commit }, { order }) {
		commit(types.ADMIN_UPSERT_ORDER, order);
	},

	[types.ADMIN_SELECT_DEPARTMENT]: ({commit}, departmentId) => {
		commit(types.ADMIN_SELECT_DEPARTMENT, departmentId);
	},

	deleteOrder({ commit }, { id, deleteData}) {
		return OrdersProvider.deleteOrder(id).then(response => {
			commit(types.ADMIN_ORDER_REMOVE, id);
			return response;
		}).then(() => {
			return OrdersProvider.changeOrder(id, deleteData).then(response => {
				commit(types.ADMIN_ORDER_UPDATE, id);
				return response;
			});
		});
	},

	orderFormEditProductSelection({ commit }, { productSelection, allowBulkOrderAdd = false, remove = false }) {
		commit(types.ADMIN_ORDER_FORM_EDIT_PRODUCT_SELECTION, { productSelection, allowBulkOrderAdd, remove });
	},

	[types.ADMIN_ORDER_BULK_FORM_NEW_ORDER_ADD]({ commit },{ dataForOrder, isEditModeActive}) {
		commit(types.ADMIN_ORDER_BULK_FORM_NEW_ORDER_ADD, {dataForOrder, isEditModeActive});
	},

	orderFormEditData({ commit }, orderData) {
		commit(types.ADMIN_ORDER_FORM_EDIT_ORDER_DATA, orderData);
	},

	orderFormResetData({ commit }) {
		commit(types.ADMIN_ORDER_FORM_RESET_ORDER_DATA);
	},
	[types.ADMIN_ORDER_BULK_FORM_RESET_ORDER_DATA]({ commit }) {
		commit(types.ADMIN_ORDER_BULK_FORM_RESET_ORDER_DATA);
	},

	orderFormSubmitOrder({ commit, getters }) {
		return OrdersProvider.sendOrder({
			pickupDate: getters.orderForm.pickupData.pickupDate,
			pickupTime: getters.orderForm.pickupData.pickupTime,
			productSelections: getters.orderForm.productSelections,
			customerDetails: getters.orderForm.customerDetails,
			orderOptions: {
				mustBeHeated: getters.orderForm.mustBeHeated,
				paymentLinkThroughEmail: getters.orderForm.isPrepaid,
				...getters.orderForm.tenantSpecificData
			},
			specialNotes: getters.orderForm.specialNotes,
			userConsent: false,
			redeemedVouchers: getters.orderForm.vouchersRedeemed,
			isAdmin: true,
			isPrepaid: getters.orderForm.isPrepaid,
			prepaidUrlAccept: `${window.location.origin}/payment/accept`,
			prepaidUrlCancel: `${window.location.origin}/payment/cancel`,
			prepaidUrlCheckout: window.location.origin
		}).then((response) => {
			check.string.orFail(response.data.orderNumber);

			commit(types.ADMIN_ORDER_FORM_RESET_ORDER_DATA);

			return response.data;
		});
	},

	sendOrderMessageToCustomer({ commit, getters }, { orderId, message }) {
		return OrderCorrespondenceProvider.createNewMessageManager({
			orderId: orderId,
			message: message.trim()
		}, getters.jwtToken).then(result => {
			commit(types.ADMIN_UPDATE_NEW_ORDER_FLAG, {
				orderId,
				flag: false
			});

			return result;
		});
	},

	setOrderMessageAsRead({ commit, getters }, { orderId }) {
		return OrderCorrespondenceProvider.setOrderMessageAsRead({
			orderId
		}, getters.jwtToken).then(result => {
			commit(types.ADMIN_UPDATE_NEW_ORDER_FLAG, {
				orderId,
				flag: false
			});

			return result;
		});
	},

	setSelectedProduct({ commit }, { productId }) {
		commit(types.ADMIN_SET_SELECTED_PRODUCT, {
			productId
		});
	},

	setCatalogEditingState({ commit }, { newStateValue }) {
		commit(types.ADMIN_SET_CATALOG_EDITING_STATE, {
			newStateValue
		});
	},

	setSelectedCatalog({ commit }, catalog) {
		commit(types.ADMIN_SET_SELECTED_CATALOG, catalog);
	},

	setSelectedCatalogId({ commit }, catalogId) {
		commit(types.ADMIN_SET_SELECTED_CATALOG_ID, catalogId);
	},

	setTempCatalog({ commit }, { catalog }) {
		commit(types.ADMIN_SET_TEMP_CATALOG, {
			catalog
		});
	},

	setCatalogMainLoading({ commit }, isLoading) {
		commit(types.ADMIN_SET_CATALOG_MAIN_LOADING, isLoading);
	},

	setProductEditingState({ commit }, newStateValue) {
		commit(types.ADMIN_SET_PRODUCT_EDITING_STATE, {
			newStateValue
		});
	},

	setProductMassEditingState({ commit }, newStateValue) {
		commit(types.ADMIN_SET_PRODUCT_MASS_EDITING_STATE, {
			newStateValue
		});
	},

	removeOrderFromBulk({ commit }, payload) {
		commit(types.ADMIN_ORDER_BULK_FORM_REMOVE_ORDER_DATA, payload);
	},
	duplicateOrderFormBulk({ commit }, index){
		commit(types.ADMIN_ORDER_BULK_FORM_DUPLICATE_ORDER_DATA, index);
	},
	[types.ADMIN_ORDER_BULK_FORM_EDIT_ORDER_DATA]({commit}, index){
		commit(types.ADMIN_ORDER_BULK_FORM_EDIT_ORDER_DATA, index);
	},
	async sendBulkOrders({ state, dispatch, getters }, { options }) {
		try{
			const arrayToSend = state.persistedData.orderFormBulk
				.map((order) => "-".repeat(+order.amount).split("").map(() => order.orderData))
				.flat()
				.map(order =>{
					order.shopId = order.shopData.id;
					order.isPrepaid = true;
					return order;
				});
			await OrderGenerateProvider.generateOrders({ data: arrayToSend, options: options }, getters.jwtToken);
			dispatch(types.ADMIN_ORDER_BULK_FORM_RESET_ORDER_DATA);
		}
		catch(err){
			dispatch("ADD_GLOBAL_ERROR", {friendlyMessage: "Minimum product amount isn't reached!"});
		}
	},
	openNavBar({ commit }) {
		commit(types.OPEN_NAV_BAR);
	},
	closeNavBar({ commit }) {
		commit(types.CLOSE_NAV_BAR);
	},
	setSelectedVersion({ commit }, newStateValue) {
		commit(types.ADMIN_SET_SELECTED_VERSION, {
			newStateValue
		});
	},
	async setProductCatalogByVersions({ commit, getters }, versionId) {
		const useNewProductCatalogFT = getters.featureToggles.useNewProductCatalog;
		const useNestOrderPaymentFlow = getters.featureToggles.useNestOrderPaymentFlow;
		const data = await ProductCatalogProvider.getProductCatalogData({
			versionId,
			useNewProductCatalog: !!useNewProductCatalogFT,
			useNestOrderPaymentFlow
		});

		commit(types.ADMIN_SET_PRODUCT_CATALOG_BY_VERSIONS, {
			data, versionId
		});
	},
	setProductSnackBarState({ commit }, {message, isShown}) {
		commit(types.SET_ADMIN_PRODUCT_SNACKBAR_STATE, {
			message,
			isShown
		});
	},
	setIsInitGboAppValue({ commit }, value) {
		commit(types.ADMIN_INIT_GBO_APP, {
			value
		});
	},
	setGoogleAuthState({ commit }, value) {
		commit(types.ADMIN_GOOGLE_AUTH_STATE, value);
	},
	setADAuthState({ commit }, value) {
		commit(types.ADMIN_AD_AUTH_STATE, value);
	},
	async findAllAdminHomePageLinks({ commit, state }) {
		const data = await AdminHomePageLinksProvider.findAll(state.persistedData.jwtToken);
		commit(types.ADMIN_HOME_PAGE_LINKS, data);
	},
	async createAdminHomePageLink({ commit, state }, createData) {
		const data = await AdminHomePageLinksProvider.create(createData, state.persistedData.jwtToken);
		const temp = [...state.adminHomePageLinks];
		temp.push({
			name: data.name,
			path: data.path,
			id: data.id,
			pagePermission: data.pagePermission,
			tenantId: data.tenantId,
			icon: data.icon,
			createdAt: data.createdAt
		});
		commit(types.ADMIN_HOME_PAGE_LINKS, temp);
	},
	async deleteAdminHomePageLink({ commit, state }, linkId) {
		const data = await AdminHomePageLinksProvider.remove(linkId, state.persistedData.jwtToken);
		commit(types.ADMIN_HOME_PAGE_LINKS, state.adminHomePageLinks.filter((link) => link.id !== data.id));
	},
	async updateAdminHomePageLink({ commit, state }, { id, updateData }) {
		const data = await AdminHomePageLinksProvider.update(id, updateData, state.persistedData.jwtToken);
		const temp = [...state.adminHomePageLinks];
		const itemIndex = temp.findIndex((link) => link.id === data.id);
		temp.splice(itemIndex, 1, data);
		commit(types.ADMIN_HOME_PAGE_LINKS, temp);
	},
	async setNavMenuItems({ commit }, newItems) {
		commit(types.ADMIN_SET_NAV_MENU_ITEMS, newItems);
	},
	async jwt1ToJwt2({ commit }, { accessToken }) {
		try {
			const response2 = await AuthenticationProvider.jwt1ToJwt2({accessToken});
			commit(types.ADMIN_SET_JWT_TOKEN2, response2.data.accessToken);
		} catch (err) {
			// console.log("jwt1ToJwt2 login problem", err);
		}
	}
};


// mutations
const mutations = {
	[types.ADMIN_REFRESH_AD_LOGIN_STATE] (state) {
		state.persistedData.azureADLoginState = uuidv4();
	},
	[types.ADMIN_REFRESH_GOOGLE_AUTH_LOGIN_STATE] (state) {
		state.persistedData.googleAuthLoginState = uuidv4();
	},
	[types.ADMIN_SET_JWT_TOKEN] (state, jwtToken) {
		state.persistedData.jwtToken = jwtToken;
	},
	[types.ADMIN_SET_JWT_TOKEN2] (state, jwtToken) {
		state.persistedData.jwtToken2 = jwtToken;
	},
	[types.ADMIN_SET_ADMIN_DETAILS] (state, adminDetails) {
		state.adminDetails = adminDetails;
	},
	[types.ADMIN_REMOVE_JWT_TOKEN] (state) {
		state.persistedData.jwtToken = null;
		state.persistedData.jwtToken2 = null;
		state.adminDetails = null;
	},
	[types.ADMIN_SET_REDIRECT_URL] (state, redirectUrl) {
		state.redirectUrl = redirectUrl;
	},
	[types.ADMIN_ORDER_SET_DATE_FILTER] (state, dateFilter) {
		state.persistedData.dateFilter = dateFilter;
	},
	[types.ADMIN_ORDER_SET_ORDERS] (state, orders) {
		state.loadedOrders = orders;
	},
	[types.ADMIN_ORDER_TOTAL_PENDING_ORDERS] (state, pendingOrders) {
		state.pendingOrders = pendingOrders;
	},
	[types.ADMIN_ORDER_CONFIRM] (state, id) {
		const foundOrder = state.loadedOrders.find(order => order.extId === id);
		foundOrder.status = "confirmed";
		foundOrder.pendingOverdue = false;
		state.pendingOrders = state.pendingOrders.filter(order => order.extId !== id);
	},
	[types.ADMIN_ORDER_REJECT] (state, id) {
		const foundOrder = state.loadedOrders.find(order => order.extId === id);
		foundOrder.status = "rejected";
		foundOrder.pendingOverdue = false;
		state.pendingOrders = state.pendingOrders.filter(order => order.extId !== id);
	},
	[types.ADMIN_ORDER_REMOVE] (state, id) {
		state.loadedOrders = state.loadedOrders.filter(order => order.extId !== id);
		state.pendingOrders = state.pendingOrders.filter(order => order.extId !== id);
	},
	[types.ADMIN_ORDER_UPDATE] (state, id) {
		state.loadedOrders = state.loadedOrders.filter(order => order.extId !== id);
		state.pendingOrders = state.pendingOrders.filter(order => order.extId !== id);
	},
	[types.ADMIN_SELECT_DEPARTMENT] (state, departmentId) {
		state.selectDepartmentId = departmentId;
	},
	[types.ADMIN_UPSERT_ORDER] (state, order) {
		const foundOrder = state.loadedOrders.find(o => o.extId === order.extId);
		if (foundOrder) {
			Object.assign(foundOrder, order);
		}
		else {
			state.loadedOrders.push(order);
			if (order.status === "pending") {
				state.pendingOrders.push(order);
			}
		}
	},

	[types.ADMIN_ORDER_FORM_EDIT_PRODUCT_SELECTION] (state, { productSelection, allowBulkOrderAdd, remove = false }) {
		const orderFormEdited = allowBulkOrderAdd
			? selectEditingBulkOrder(state)
			: state.persistedData.orderForm;
		if (allowBulkOrderAdd) {
			if (!remove) {
				const productIndex = orderFormEdited.orderData.productSelections.findIndex(ps => ps.productId === productSelection.productId);
				if (productIndex > -1) {
					const currentSelection = orderFormEdited.orderData.productSelections[productIndex];
					orderFormEdited.orderData.productSelections[productIndex] = Object.assign(currentSelection, productSelection);
				} else {
					orderFormEdited.orderData.productSelections.push(productSelection);
				}
			} else {
				orderFormEdited.orderData.productSelections = orderFormEdited.orderData.productSelections
					.filter(ps => ps.productId !== productSelection.productId);
			}
		} else {
			if (!remove) {
				const productSelectionIndex = orderFormEdited.productSelections.findIndex(ps => ps.productSelectionId === productSelection.productSelectionId);
				if (productSelectionIndex > -1) {
					const currentSelection = orderFormEdited.productSelections[productSelectionIndex];
					orderFormEdited.productSelections[productSelectionIndex] = Object.assign(currentSelection, productSelection);
				} else {
					orderFormEdited.productSelections.push(productSelection);
				}
			} else {
				orderFormEdited.productSelections = orderFormEdited.productSelections.filter(ps => ps.productSelectionId !== productSelection.productSelectionId);
				// orderFormEdited.productSelections = orderFormEdited.productSelections
				// 	.filter(ps => ps.productId !== productSelection.productId);
			}
		}
	},

	[types.ADMIN_ORDER_BULK_FORM_NEW_ORDER_ADD] (state, {dataForOrder, isEditModeActive}) {
		const { amountOfOrder, data } = copyObject(dataForOrder);
		const newOrderData = {
			amount: amountOfOrder,
			orderData: {
				...copyObject(initialOrderForm),
				...data
			}
		};
		if(!isEditModeActive){
			state.persistedData.orderFormBulk = [newOrderData].concat(...state.persistedData.orderFormBulk);
		}else{
			const newOrderEditData ={
				amount: amountOfOrder,
				orderData: {
					...copyObject(selectEditingBulkOrder(state).orderData),
					...data
				}
			};
			state.persistedData.orderFormBulk = state.persistedData.orderFormBulk.map((e,i) => {
				if(i === state.indexOfEditingOrderFromBulk){ return newOrderEditData;}else{ return e;}
			});
		}

	},
	[types.ADMIN_ORDER_FORM_EDIT_ORDER_DATA] (state, {
		shopData = null, pickupData = null, customerDetails = null, mustBeHeated = null, specialNotes = null, isPrepaid = null,
		vouchersRedeemed = [], tenantSpecificData = null
	}) {
		if (shopData !== null) {
			state.persistedData.orderForm.shopData = copyObject(shopData);
		}

		if (pickupData !== null) {
			state.persistedData.orderForm.pickupData = copyObject(pickupData);
		}

		if (customerDetails !== null) {
			state.persistedData.orderForm.customerDetails = copyObject(customerDetails);
		}

		if (mustBeHeated !== null) {
			state.persistedData.orderForm.mustBeHeated = mustBeHeated;
		}

		if (mustBeHeated !== null) {
			state.persistedData.orderForm.specialNotes = specialNotes;
		}

		if (isPrepaid !== null) {
			state.persistedData.orderForm.isPrepaid = isPrepaid;
		}

		if (tenantSpecificData !== null) {
			state.persistedData.orderForm.tenantSpecificData = copyObject(tenantSpecificData);
		}

		if (vouchersRedeemed !== []) {
			state.persistedData.orderForm.vouchersRedeemed = copyObject(vouchersRedeemed);
		}
	},

	[types.ADMIN_ORDER_FORM_RESET_ORDER_DATA] (state) {
		state.persistedData.orderForm = copyObject(initialOrderForm);
	},
	[types.ADMIN_ORDER_BULK_FORM_RESET_ORDER_DATA](state){
		state.persistedData.orderFormBulk = initialBulkOrder;
	},

	[types.ADMIN_UPDATE_NEW_ORDER_FLAG] (state, { orderId, flag }) {
		const order = state.loadedOrders.find(loadedOrder => loadedOrder.extId === orderId);
		if (order) {
			order.newOrderMessageFlag = flag;
		}
	},

	[types.ADMIN_SET_SELECTED_PRODUCT] (state, { productId }) {
		state.persistedData.products.selectedProductId = productId;
	},

	[types.ADMIN_SET_CATALOG_EDITING_STATE] (state, { newStateValue }) {
		state.catalogEditingState = newStateValue;
	},

	[types.ADMIN_SET_SELECTED_CATALOG] (state, catalog) {
		state.selectedCatalog = catalog;
	},

	[types.ADMIN_SET_SELECTED_CATALOG_ID] (state, catalogId) {
		state.persistedData.catalogs.selectedCatalogId = catalogId;
	},

	[types.ADMIN_SET_TEMP_CATALOG] (state, { catalog }) {
		state.persistedData.catalogs.tempCatalog = catalog;
	},

	[types.ADMIN_SET_CATALOG_MAIN_LOADING] (state, isLoading) {
		state.persistedData.catalogs.isCatalogPagesMainLoading = isLoading;
	},

	[types.ADMIN_SET_PRODUCT_EDITING_STATE] (state, { newStateValue }) {
		state.productEditingState = newStateValue;
	},

	[types.ADMIN_SET_PRODUCT_MASS_EDITING_STATE] (state, { newStateValue }) {
		state.productMassEditingState = newStateValue;
	},

	[types.ADMIN_ORDER_BULK_FORM_REMOVE_ORDER_DATA] (state, orderIndex) {
		state.persistedData.orderFormBulk = state.persistedData.orderFormBulk.filter((order, index) => orderIndex !== index);
	},
	[types.ADMIN_ORDER_BULK_FORM_DUPLICATE_ORDER_DATA](state, orderIndex){
		const orderToDuplicate = state.persistedData.orderFormBulk[orderIndex];
		state.persistedData.orderFormBulk.splice(orderIndex,0,orderToDuplicate);
	},
	[types.ADMIN_ORDER_BULK_FORM_EDIT_ORDER_DATA](state, orderIndex){
		state.indexOfEditingOrderFromBulk = orderIndex;
	},
	[types.OPEN_NAV_BAR] (state) {
		state.isNavBarOpen = true;
	},
	[types.CLOSE_NAV_BAR] (state) {
		state.isNavBarOpen = false;
	},
	[types.ADMIN_SET_PRODUCT_CATALOG_BY_VERSIONS] (state, { versionId, data }) {
		state.productCatalogByVersions[versionId] = data;
	},
	[types.ADMIN_SET_SELECTED_VERSION] (state, { newStateValue }) {
		state.selectedVersion = newStateValue;
	},
	[types.SET_ADMIN_PRODUCT_SNACKBAR_STATE] (state, { message, isShown }) {
		state.productSnackBar.isShown = isShown;
		state.productSnackBar.message = message;
	},
	[types.ADMIN_INIT_GBO_APP] (state, { value }) {
		state.isInitGboApp = value;
	},
	[types.ADMIN_GOOGLE_AUTH_STATE] (state, payload) {
		state.persistedData.googleAuthState = payload;
	},
	[types.ADMIN_AD_AUTH_STATE] (state, payload) {
		state.persistedData.adAuthState = payload;
	},
	[types.ADMIN_HOME_PAGE_LINKS] (state, payload) {
		state.adminHomePageLinks = payload;
	},
	[types.ADMIN_SET_NAV_MENU_ITEMS] (state, payload) {
		state.navMenuItems = payload;
	}
};

export default {
	state,
	getters,
	actions,
	mutations
};
